// BetaTech (2026-10-20, client request): estimated delivery dates on the retail product page
// ("Arritja: 21–22 tetor", snippets/beta-pdp-info.liquid).
//
// Computed in the browser, because product pages are cached: a date printed by Liquid would go stale.
// Rules (settings in Theme settings → "BetaTech – Faqja e produktit", passed as JSON by the snippet):
// - "today" and the hour are taken in the shop's time zone (Kosovo, Europe/Belgrade), whatever the
//   visitor's device says;
// - at or after the cutoff hour (default 15:00) the order counts from the next day;
// - with skipSunday, Sunday is neither a sending day nor a delivery day: a start day on Sunday (an order
//   on Sunday, or on Saturday after the cutoff) moves to Monday, and Sundays are never counted or shown;
// - the window is min..max delivery days after that start day (default 1..2; min 0 = the start day);
// - the same month prints as "21–22 tetor", two months as "31 tetor – 2 nëntor", one day as "21 tetor"
//   (the word order and the month names come from the theme locale, so /en shows "October 21–22").
// The pure functions (window.BetaPdpDelivery) are unit-tested with fixed dates in
// tests/beta-pdp-delivery.jxa.js; the DOM part below only runs in a browser.
//
// BetaTech (2026-10-21, "PDP round 2"): the same variant event also drives
// - the "Kodi" (SKU) row of the info table: the selected variant's SKU, the row hidden for a variant
//   without SKU or an option combination that does not exist;
// - the sold-out photo (snippets/beta-pdp-soldout.liquid): the centred "Nuk ka në stok" and the blur
//   (assets/beta-storefront.css section 12d) show for an existing variant that cannot be bought and hide
//   for an available one or a combination that does not exist.
// BetaTech (2026-10-27, client request: stock in the table; "the delivery is not shown right for Kosovo / Albania /
// Macedonia"):
// - the delivery window and price follow the visitor's market country (the table's data-beta-country, from
//   localization.country.iso_code): Albania and North Macedonia ("abroad": abroadMin..abroadMax days, abroadPrice),
//   Kosovo and every other country (min..max, price); the row reads "21–22 tetor · dërgesa 2 €" (forCountry(), arrivalText());
// - the "Stoku" row: "Ka stok" / "Ka pak copë" / "Me porosi" / "Nuk ka në stok" for the selected variant (stockState();
//   the per-variant states come from Liquid in cfg.stock, so no quantity is exposed);
// - the "Arritja" row only for a variant that can be bought from stock: hidden when sold out and when sold "Me porosi"
//   (deliveryAvailable(): the delivery window does not hold for an item ordered from the supplier);
// - the "Kodi" row of the "Specifikat" card (sections/product-details.liquid, retail and B2B) follows the main
//   product section's variant picker.
// Loaded once per product page by snippets/product-template.liquid.
window.BetaPdpDelivery = window.BetaPdpDelivery || (function () {
  var DAY_MS = 86400000;
  var DEFAULTS = {
    min: 1,
    max: 2,
    skipSunday: true,
    cutoff: 15,
    timeZone: 'Europe/Belgrade',
    months: ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'],
    range: '[from]–[to] [month]',
    rangeMonths: '[from] [from_month] – [to] [to_month]',
    day: '[day] [month]',
    price: '',
    abroadMin: 2,
    abroadMax: 4,
    abroadPrice: '',
    abroadCountries: ['AL', 'MK'],
    withPrice: '[arrival] · [price]'
  };
  var STOCK_STATES = { 'in': true, 'low': true, 'order': true, 'out': true };

  function option(cfg, key) {
    return cfg && cfg[key] !== undefined && cfg[key] !== null ? cfg[key] : DEFAULTS[key];
  }

  function toInt(value, fallback) {
    var number = parseInt(value, 10);
    return isNaN(number) ? fallback : number;
  }

  // { year, month (1-12), day, hour (0-23) } of `date` in `timeZone`. Without time zone support in the
  // browser, the device's own clock is used.
  function localParts(date, timeZone) {
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || DEFAULTS.timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(date).forEach(function (part) {
        parts[part.type] = part.value;
      });
      var result = {
        year: parseInt(parts.year, 10),
        month: parseInt(parts.month, 10),
        day: parseInt(parts.day, 10),
        hour: parseInt(parts.hour, 10) % 24
      };
      if (result.year && result.month && result.day && result.hour >= 0) return result;
    } catch (error) {
      // no Intl time zone support: fall through to the device clock
    }
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours() };
  }

  // Calendar dates are kept as UTC midnights, so adding days never meets a daylight-saving change.
  function calendarDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  function isClosed(date, cfg) {
    return option(cfg, 'skipSunday') !== false && date.getUTCDay() === 0;
  }

  // The first day the shop can send the order: `date` itself, or the next day that is not closed.
  function nextOpenDay(date, cfg) {
    while (isClosed(date, cfg)) date = new Date(date.getTime() + DAY_MS);
    return date;
  }

  // Counts `days` delivery days after the start day (an open day: see arrival()); closed days are
  // skipped. Day 0 is the start day itself.
  function addDeliveryDays(start, days, cfg) {
    var date = start;
    for (var i = 0; i < days; i++) date = nextOpenDay(new Date(date.getTime() + DAY_MS), cfg);
    return date;
  }

  // now: { year, month, day, hour } in shop time. Returns { from, to } as UTC-midnight dates.
  // The start day is today, or tomorrow at / after the cutoff hour; a Sunday start (an order on Sunday,
  // or on Saturday after the cutoff) moves to Monday, because nothing is sent on Sunday: Sat 16:00,
  // Sun 10:00 and Sun 16:00 all count from Monday (→ Tue–Wed), Sat 10:00 counts from Saturday (→ Mon–Tue).
  function arrival(now, cfg) {
    var max = Math.max(0, toInt(option(cfg, 'max'), DEFAULTS.max));
    var min = Math.min(Math.max(0, toInt(option(cfg, 'min'), DEFAULTS.min)), max);
    var cutoff = toInt(option(cfg, 'cutoff'), DEFAULTS.cutoff);
    var start = calendarDate(now.year, now.month, now.day);
    if (now.hour >= cutoff) start = new Date(start.getTime() + DAY_MS);
    start = nextOpenDay(start, cfg);
    return { from: addDeliveryDays(start, min, cfg), to: addDeliveryDays(start, max, cfg) };
  }

  function fill(template, values) {
    return String(template).replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function format(range, cfg) {
    var months = option(cfg, 'months');
    var from = range.from;
    var to = range.to;
    var fromMonth = months[from.getUTCMonth()] || DEFAULTS.months[from.getUTCMonth()];
    var toMonth = months[to.getUTCMonth()] || DEFAULTS.months[to.getUTCMonth()];
    var fromDay = String(from.getUTCDate());
    var toDay = String(to.getUTCDate());

    if (from.getTime() === to.getTime()) {
      return fill(option(cfg, 'day'), { day: fromDay, month: fromMonth });
    }
    if (from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth()) {
      return fill(option(cfg, 'range'), { from: fromDay, to: toDay, month: fromMonth });
    }
    return fill(option(cfg, 'rangeMonths'), { from: fromDay, from_month: fromMonth, to: toDay, to_month: toMonth });
  }

  function estimate(date, cfg) {
    return format(arrival(localParts(date, option(cfg, 'timeZone')), cfg), cfg);
  }

  // The variant picker's event detail (assets/variant-picker.js) → what the page shows: currentVariant is the
  // variant JSON, or null when the chosen option combination does not exist (isOptionExist false).
  // { exists, available, sku }: the SKU row shows sku (hidden when ''), the delivery row needs exists and
  // available, the sold-out photo shows for exists and not available.
  function variantState(detail) {
    detail = detail || {};
    var variant = detail.isOptionExist !== false ? detail.currentVariant : null;
    var sku = variant && variant.sku !== undefined && variant.sku !== null ? String(variant.sku).trim() : '';
    return { exists: !!variant, available: !!(variant && variant.available), sku: sku };
  }

  // Delivery zone of a market country (ISO code, e.g. "XK", "AL"): 'abroad' for the abroadCountries (Albania and
  // North Macedonia), 'XK' for Kosovo, every other country and a missing code.
  function zoneOf(country, cfg) {
    var code = String(country || '').replace(/^\s+|\s+$/g, '').toUpperCase();
    var list = option(cfg, 'abroadCountries') || [];
    for (var i = 0; i < list.length; i++) {
      if (code && String(list[i]).toUpperCase() === code) return 'abroad';
    }
    return 'XK';
  }

  // A copy of cfg whose min / max / price are those of the country's zone (unchanged for Kosovo), for estimate() and
  // arrivalText().
  function forCountry(cfg, country) {
    var out = {};
    cfg = cfg || {};
    for (var key in cfg) {
      if (Object.prototype.hasOwnProperty.call(cfg, key)) out[key] = cfg[key];
    }
    if (zoneOf(country, cfg) === 'abroad') {
      out.min = option(cfg, 'abroadMin');
      out.max = option(cfg, 'abroadMax');
      out.price = option(cfg, 'abroadPrice');
    }
    return out;
  }

  // The "Arritja" row: the dates, and the delivery price when there is one ("21–22 tetor · dërgesa 2 €"; the pattern is cfg.withPrice, from the locale).
  function arrivalText(date, cfg) {
    var dates = estimate(date, cfg);
    var price = option(cfg, 'price');
    price = price === undefined || price === null ? '' : String(price);
    return price ? fill(option(cfg, 'withPrice'), { arrival: dates, price: price }) : dates;
  }

  // The "Stoku" row for the variant picker's event detail: 'in' (Ka stok), 'low' (Ka pak copë), 'order' (Me porosi),
  // 'out' (Nuk ka në stok), or '' (row hidden) for an option combination that does not exist. cfg.stock maps variant ids to
  // the state Liquid computed with the quantities when the page was printed; the event's availability wins when the
  // two disagree (a variant that cannot be bought is 'out', an available one is never 'out'). A variant missing from
  // the map (over 250 variants) is 'in' or 'out'.
  function stockState(detail, cfg) {
    var state = variantState(detail);
    if (!state.exists) return '';
    if (!state.available) return 'out';
    var id = detail.currentVariant.id;
    var map = cfg && cfg.stock;
    var mapped = map && id !== undefined && id !== null && Object.prototype.hasOwnProperty.call(map, String(id)) ? map[String(id)] : '';
    return mapped && STOCK_STATES[mapped] === true && mapped !== 'out' ? mapped : 'in';
  }

  // The "Arritja" row for the variant picker's event detail: shown only for a variant that exists, can be bought and
  // is not sold "Me porosi" (stock state 'order').
  function deliveryAvailable(detail, cfg) {
    var state = variantState(detail);
    return state.exists && state.available && stockState(detail, cfg) !== 'order';
  }

  return {
    localParts: localParts,
    arrival: arrival,
    format: format,
    estimate: estimate,
    variantState: variantState,
    zoneOf: zoneOf,
    forCountry: forCountry,
    arrivalText: arrivalText,
    stockState: stockState,
    deliveryAvailable: deliveryAvailable
  };
})();

(function () {
  if (typeof document === 'undefined' || window.BetaPdpDelivery.domReady) return;
  window.BetaPdpDelivery.domReady = true;

  var VARIANT_EVENT = (typeof PRODUCT_EVENT !== 'undefined' && PRODUCT_EVENT.updatedVariant) || 'maximize:variant-picker:updated:';

  function setup(root) {
    if (root.getAttribute('data-beta-pdp-ready') === 'true') return;
    var configElement = root.querySelector('script[data-beta-pdp-config]');
    var row = root.querySelector('[data-beta-arrival]');
    var text = root.querySelector('[data-beta-arrival-text]');
    var skuRow = root.querySelector('[data-beta-sku]');
    var skuText = root.querySelector('[data-beta-sku-text]');
    var stockRow = root.querySelector('[data-beta-stock]');
    var stockState = root.querySelector('[data-beta-stock-state]');
    var stockText = root.querySelector('[data-beta-stock-text]');
    if (!configElement || !row || !text) return;

    var cfg;
    try {
      cfg = JSON.parse(configElement.textContent);
    } catch (error) {
      return; // keep the text printed by Liquid
    }
    root.setAttribute('data-beta-pdp-ready', 'true');

    // Window and price of the visitor's market country (Kosovo / Albania, North Macedonia).
    var zoneCfg = window.BetaPdpDelivery.forCountry(cfg, root.getAttribute('data-beta-country'));

    // Liquid hides the row when the variant on the page cannot be bought from stock.
    var available = !row.hasAttribute('hidden');

    function render() {
      if (!root.isConnected) return;
      try {
        text.textContent = window.BetaPdpDelivery.arrivalText(new Date(), zoneCfg);
      } catch (error) {
        // keep the previous text
      }
      row.hidden = !available;
    }

    render();

    // Variant picker (main form or sticky bar): show the row only for a variant that exists and can be bought from
    // stock (not "Me porosi"); the SKU row shows the variant's SKU, or hides when it has none.
    document.addEventListener(VARIANT_EVENT + cfg.sectionId, function (event) {
      var state = window.BetaPdpDelivery.variantState(event.detail);
      available = window.BetaPdpDelivery.deliveryAvailable(event.detail, cfg);
      render();
      if (skuRow && skuText) {
        if (state.sku) skuText.textContent = state.sku;
        skuRow.hidden = !state.sku;
      }
      if (stockRow && stockState && stockText) {
        var stock = window.BetaPdpDelivery.stockState(event.detail, cfg);
        stockRow.hidden = !stock;
        if (stock) {
          stockState.setAttribute('data-state', stock);
          if (cfg.stockText && cfg.stockText[stock]) stockText.textContent = cfg.stockText[stock];
        }
      }
    });

    // A tab left open can pass the cutoff hour or midnight.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) render();
    });
    window.addEventListener('pageshow', render);
  }

  // Sold-out photo: the element is printed for every retail product page, hidden while the variant can be bought.
  function setupSoldout(element) {
    if (element.getAttribute('data-beta-pdp-ready') === 'true') return;
    var sectionId = element.getAttribute('data-section-id');
    if (!sectionId) return;
    element.setAttribute('data-beta-pdp-ready', 'true');
    document.addEventListener(VARIANT_EVENT + sectionId, function (event) {
      if (!element.isConnected) return;
      var state = window.BetaPdpDelivery.variantState(event.detail);
      element.hidden = !(state.exists && !state.available);
    });
  }

  // "Specifikat" card (sections/product-details.liquid, retail and B2B): its "Kodi" row follows the variant picker of
  // the main product section, whose id is read from that section's #CurrentVariantInfo__<section id>.
  function mainSectionId() {
    var info = document.querySelector('.section-main-product script[id^="CurrentVariantInfo__"]');
    return info ? info.id.slice('CurrentVariantInfo__'.length) : '';
  }

  function setupSpecs(card) {
    if (card.getAttribute('data-beta-pdp-ready') === 'true') return;
    var row = card.querySelector('[data-beta-specs-sku]');
    var text = card.querySelector('[data-beta-specs-sku-text]');
    var sectionId = mainSectionId();
    if (!row || !text || !sectionId) return;
    card.setAttribute('data-beta-pdp-ready', 'true');
    document.addEventListener(VARIANT_EVENT + sectionId, function (event) {
      if (!card.isConnected) return;
      var state = window.BetaPdpDelivery.variantState(event.detail);
      if (state.sku) text.textContent = state.sku;
      row.hidden = !state.sku;
    });
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-beta-pdp-info]');
    for (var i = 0; i < roots.length; i++) setup(roots[i]);
    var soldouts = (scope || document).querySelectorAll('[data-beta-pdp-soldout]');
    for (var j = 0; j < soldouts.length; j++) setupSoldout(soldouts[j]);
    var cards = (scope || document).querySelectorAll('[data-beta-specs]');
    for (var k = 0; k < cards.length; k++) setupSpecs(cards[k]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
